/**
 * What this console session actually is. Chrome furniture, not a route -- see `Popover.tsx`.
 *
 * THERE IS NO SETTINGS SPECIFICATION ANYWHERE IN THE PLAN. No section of `CONSOLES.md` §2, no
 * entry in `c-fleet-console.md`, no route in the manifest. So rather than invent product --
 * preferences nobody asked for, toggles with nothing behind them -- this panel reports facts
 * that are already true and that an operator currently has no way to see:
 *
 *   which API this console is pointed at, and which implementation it therefore selected
 *   who is signed in
 *   which definition kinds and message types the contract carries
 *
 * Every one of those has cost somebody an hour at some point. "Am I looking at the mock or the
 * real fleet?" is the question this console is least able to answer from its own screen, and it
 * is the question behind the audit finding that nine screens read a mock regardless of
 * configuration. Surfacing it is worth more than a theme switch.
 *
 * When settings IS specified, this is where it lands and the shape below is the honest
 * placeholder rather than a guess at the answer.
 */
import { configuredBaseUrl, selectImplementation, useOptionalFleetApi } from '../../api/index.ts'

export interface SettingsPanelProps {
  /** The signed-in principal, when there is one. Absent on the demo path. */
  account?: { name?: string; username?: string }
  /** Rendered at the foot when the authenticated tree supplies one. */
  accountAction?: React.ReactNode
}

function Row({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div className="settings-row">
      <span className="settings-label">{label}</span>
      <span className="settings-value">{value}</span>
      {note ? <span className="settings-note">{note}</span> : null}
    </div>
  )
}

export function SettingsPanel({ account, accountAction }: SettingsPanelProps) {
  const baseUrl = configuredBaseUrl()
  const implementation = selectImplementation(baseUrl)
  const api = useOptionalFleetApi()

  return (
    <div className="settings">
      <Row
        label="Fleet API"
        value={baseUrl ?? 'Not configured'}
        note={
          baseUrl
            ? undefined
            : 'VITE_API_BASE_URL is unset, so this console is running against the seeded mock.'
        }
      />
      <Row
        label="Implementation"
        value={implementation === 'http' ? 'HTTP · console-api' : 'Mock · seeded from fixtures'}
        note={
          // The one thing this panel exists to make visible. The fleet, deployment and library
          // screens import the mock directly and do NOT go through the seam, so on the HTTP
          // path they are still showing seeded data while the client screens show real data.
          // A console that reported one implementation while two were live would be worse than
          // no panel at all.
          implementation === 'http'
            ? 'The client screens read this API. The fleet, deployment and library screens still read the mock directly (wf-12, useFleet.ts).'
            : undefined
        }
      />
      <Row
        label="Seam mounted"
        value={api ? 'Yes' : 'No'}
        note={api ? undefined : 'No API is in the tree above this panel, so nothing can read the fleet.'}
      />
      <Row label="Signed in" value={account?.name ?? account?.username ?? 'Not signed in'} />

      {accountAction ? <div className="settings-action">{accountAction}</div> : null}
    </div>
  )
}
